import { useMutation } from '@tanstack/react-query';
import { Col, Row } from 'antd';
import { ReportApis } from 'apis/ReportApis';
import ButtonExport from 'components/Button/ButtonExport';
import { useTitle } from 'contexts/TitleProvider';
import { useUser } from 'contexts/UserProvider';
import dayjs from 'dayjs';
import usePagination from 'hooks/usePagination';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { exportExcel_v2 } from 'utils/constants/exportExcel';
import { convertDateToBE } from 'utils/constants/functions';
import FilterCustomerRevenue from './FilterTeamSaleRevenue';
import TableCustomerRevenue from './TableTeamSaleRevenue';

const TeamSaleRevenuePage = () => {
  const { user } = useUser();
  const { setTitle, setBreadcrumb } = useTitle();
  const [data, setData] = useState<any>({});
  const [isShow, setIsShow] = useState(false);
  const [filters, setFilters] = useState<any>({});
  const { page, page_size } = usePagination();
  const navigate = useNavigate();
  const ref = useRef<any>(null);

  const defaultFilter = useMemo(() => {
    const userAny = user as any;

    if (user?.plantCode === '5000') return { month: dayjs() };

    if (user?.isSale) {
      const teamSale = userAny?.teamSales?.[0];

      return {
        plantId: userAny?.plantCode,
        teamSalesId: teamSale?.id || 'SALE_ID',
        teamSalesSubId: teamSale?.subId || 'SALE_SUB_ID',
        teamSalesName: teamSale?.name,
        month: dayjs(),
      };
    }

    return {
      plantId: userAny?.plantCode,
      month: dayjs(),
    };
  }, [user]);

  const { mutate: getCustomerRevenue, isPending: getCustomerRevenueLoading } =
    useMutation({
      mutationFn: ReportApis.getTeamSaleRevenue,
      onSuccess: (data: any, variables: any) => {
        const result = data?.data?.getSalesComparisonReport || {};

        if (variables.type === 'export') {
          const items = result.items || [];

          return exportExcel_v2(
            variables.headerRow,
            items,
            'Báo cáo - Doanh thu đội bán hàng',
          );
        }

        setData(result);
      },
      onError: (error) => {
        console.log(error);
      },
    });

  const handleFilter = (values: any) => {
    setIsShow(true);
    setFilters(values);
    navigate(`?page=1&page_size=${page_size}`);
  };

  const handleCancel = () => {
    setIsShow(false);
    setFilters({ month: dayjs() });
  };

  const getParamsForData = useCallback(
    (columns?: any) => {
      const fromDate = filters?.year
        ? convertDateToBE(dayjs(filters?.year[0]))
        : null;
      const toDate = filters?.year
        ? convertDateToBE(dayjs(filters?.year[1]))
        : null;

      const day = dayjs(filters?.month);

      return {
        skip: 0,
        take: data.pageInfo?.totalItems || 1000,
        ...(filters.routeId && { routeId: filters.routeId }),
        ...(filters.teamSalesId && {
          teamSalesId: filters.teamSalesId.split('.')[0],
        }),
        ...(filters.plantId && { plantId: filters.plantId }),
        ...(fromDate && { fromDate }),
        ...(toDate && { toDate }),
        ...(day && { month_: day.get('month') + 1, year_: day.get('year') }),
        ...(columns && { headerRow: columns, type: 'export' }),
      };
    },
    [data.pageInfo?.totalItems, filters],
  );

  const exportExcel = (columns: any) => {
    getCustomerRevenue(getParamsForData(columns));
  };

  const handleExportExcel = () => {
    if (!ref.current) return;

    ref.current.handleExportExcel();
  };

  useEffect(() => {
    setTitle('Doanh thu đội bán hàng');
    setBreadcrumb([{ title: 'Báo cáo' }, { title: 'Doanh thu đội bán hàng' }]);
  }, [setBreadcrumb, setTitle]);

  useEffect(() => {
    if (!isShow) return;

    getCustomerRevenue(getParamsForData());
  }, [getCustomerRevenue, getParamsForData, isShow]);

  return (
    <>
      <Row gutter={[8, 16]} align={'middle'} justify={'end'}>
        <Col>
          <ButtonExport disabled={!isShow} onClick={handleExportExcel} />
        </Col>
      </Row>
      <FilterCustomerRevenue
        onFilter={handleFilter}
        onCancel={handleCancel}
        defaultFilter={defaultFilter}
      />

      {isShow && (
        <TableCustomerRevenue
          ref={ref}
          exportExcel={exportExcel}
          loading={getCustomerRevenueLoading}
          data={data}
        />
      )}
    </>
  );
};

export default TeamSaleRevenuePage;
